import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../context/GlobalContext";
import firestore from "../../firestore";
import {
  Bell,
  Plus,
  Pencil,
  Save,
  Trash2,
  Search,
  Activity,
  BarChart3,
  MessageSquare,
  Smartphone,
  Send,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Gift,
  Code,
  FileText,
  Eye,
  Filter,
  Shield,
  Database,
  CreditCard,
  X,
} from "lucide-react";
import { ContainerSpinner } from "../../components/Spinner";
import { Dialog } from "../../components/Dialog";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import Input from "../../components/Input";
import Textarea from "../../components/Textarea";
import Select from "../../components/Select";
import { Button } from "../../shadcn/Button";
import { unsafeAPI } from "../../hooks/api";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExitCondition {
  field: string;
  operator: "equals" | "not_equals" | "greater_than" | "less_than" | "is_null" | "is_not_null";
  value: string;
}

interface LifecycleFlowStep {
  step_order: number;
  channel: "push" | "sms" | "whatsapp";
  title: string;
  body: string;
  delay_after_previous_days: number;
  generate_discount: boolean;
  discount_value_off: number;
  discount_validity_days: number;
  exit_conditions?: ExitCondition[];
}

interface LifecycleFlow {
  id?: string;
  name: string;
  flow_type_id: string;
  flow_type: string;
  country: string;
  is_active: boolean;
  delay_days: number;
  steps: LifecycleFlowStep[];
  bigquery_query: string;
  created_at?: any;
  updated_at?: any;
  created_by?: string;
}

interface FlowType {
  id: string;
  name: string;
  display_name: string;
  description: string;
  category: string;
  bigquery_query: string;
  available_fields: string[];
  is_system: boolean;
  created_at?: any;
  updated_at?: any;
}

interface MessageTemplate {
  id: string;
  name: string;
  channel: string;
  category: string;
  title: string;
  body: string;
  is_system: boolean;
}

interface Execution {
  id: string;
  flow_id: string;
  flow_name: string;
  flow_type: string;
  started_at: string;
  status: string;
  users_targeted: number;
  notifications_sent: number;
  notifications_failed: number;
  step_index: number;
  channel: string;
}

interface FlowMetric {
  flow_id: string;
  flow_name: string;
  flow_type: string;
  is_active: boolean;
  total_targeted: number;
  total_sent: number;
  total_failed: number;
  execution_count: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  engagement: "Engagement",
  growth: "Crecimiento",
  retention: "Retención",
  win_back: "Recuperación",
  custom: "Personalizado",
};

const CATEGORY_COLORS: Record<string, string> = {
  engagement: "bg-blue-500/20 text-blue-400",
  growth: "bg-green-500/20 text-green-400",
  retention: "bg-yellow-500/20 text-yellow-400",
  win_back: "bg-purple-500/20 text-purple-400",
  custom: "bg-neutral-500/20 text-neutral-400",
};

const CHANNEL_ICONS: Record<string, any> = {
  push: Bell,
  sms: MessageSquare,
  whatsapp: Smartphone,
};

const EXIT_CONDITION_OPERATORS: { label: string; value: ExitCondition["operator"] }[] = [
  { label: "Igual a", value: "equals" },
  { label: "No igual a", value: "not_equals" },
  { label: "Mayor que", value: "greater_than" },
  { label: "Menor que", value: "less_than" },
  { label: "Es nulo", value: "is_null" },
  { label: "No es nulo", value: "is_not_null" },
];

const DEVICE_FIELDS = [
  "device_type",
  "device_manufacturer",
  "device_price",
  "amount_financed",
  "device_loan_duration",
  "device_imei_no",
  "date_bought",
  "is_released",
];

const PAYMENT_FIELDS = [
  "actualAmount",
  "amountPaid",
  "dueDate",
  "paid",
  "most_recent_paid_date",
];

const USER_FIELDS = [
  "first_name",
  "document_id",
  "push_token",
  "phone_number",
  "country",
  "email",
  "user_name",
];

const COMMON_EXTRA_FIELDS = [...DEVICE_FIELDS, ...PAYMENT_FIELDS];

// ─── Helper: SQL Preview ─────────────────────────────────────────────────────

function SqlPreview({
  sql,
  maxHeight = "max-h-48",
}: {
  sql: string;
  maxHeight?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="relative">
      <pre
        className={`overflow-auto rounded-lg bg-neutral-900 p-3 text-xs text-neutral-300 ${expanded ? "max-h-[500px]" : maxHeight}`}
      >
        <code>{sql}</code>
      </pre>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-1 text-xs text-primary-400 hover:text-primary-300"
      >
        {expanded ? "Colapsar" : "Expandir"}
      </button>
    </div>
  );
}

// ─── Helper: Available Fields Display ────────────────────────────────────────

function AvailableFieldsBadges({
  fields,
  onInsert,
  showExtras = false,
}: {
  fields: string[];
  onInsert?: (field: string) => void;
  showExtras?: boolean;
}) {
  const [showDevicePayment, setShowDevicePayment] = useState(true);

  const queryFields = fields.filter((f) => !COMMON_EXTRA_FIELDS.includes(f));
  const activeExtras = fields.filter((f) => COMMON_EXTRA_FIELDS.includes(f));

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {queryFields.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => onInsert?.(f)}
            className="inline-flex items-center gap-1 rounded-md bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300 transition-colors hover:bg-primary-500/30 hover:text-primary-300"
            title={onInsert ? `Insertar {{${f}}}` : f}
          >
            <Code className="size-3" />
            {`{{${f}}}`}
          </button>
        ))}
        {activeExtras.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => onInsert?.(f)}
            className="inline-flex items-center gap-1 rounded-md bg-blue-500/15 px-2 py-0.5 text-xs text-blue-300 transition-colors hover:bg-blue-500/30"
            title={onInsert ? `Insertar {{${f}}}` : f}
          >
            {DEVICE_FIELDS.includes(f) ? <Smartphone className="size-3" /> : <CreditCard className="size-3" />}
            {`{{${f}}}`}
          </button>
        ))}
        {fields.length > 0 && (
          <span className="inline-flex items-center rounded-md bg-neutral-800/50 px-2 py-0.5 text-xs text-neutral-500">
            {`{{discount_code}}`}
          </span>
        )}
      </div>
      {showExtras && onInsert && (
        <div>
          <button
            type="button"
            onClick={() => setShowDevicePayment(!showDevicePayment)}
            className="flex items-center gap-1.5 rounded-md bg-neutral-800/60 px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-700/60"
          >
            <Database className="size-3" />
            {showDevicePayment ? "Ocultar campos extra" : "Mostrar campos de dispositivos y pagos"}
          </button>
          {showDevicePayment && (
            <div className="mt-1 rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-2 space-y-2">
              <div>
                <p className="mb-1 flex items-center gap-1 text-xs text-neutral-500">
                  <Code className="size-3" /> Usuarios
                </p>
                <div className="flex flex-wrap gap-1">
                  {USER_FIELDS.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => onInsert(f)}
                      className="inline-flex items-center gap-1 rounded-md bg-purple-500/10 px-2 py-0.5 text-xs text-purple-400 transition-colors hover:bg-purple-500/25"
                    >
                      {`{{${f}}}`}
                    </button>
                  ))}
                  <span className="inline-flex items-center rounded-md bg-neutral-800/50 px-2 py-0.5 text-xs text-neutral-500">
                    {`{{discount_code}}`}
                  </span>
                </div>
              </div>
              <div>
                <p className="mb-1 flex items-center gap-1 text-xs text-neutral-500">
                  <Smartphone className="size-3" /> Dispositivos
                </p>
                <div className="flex flex-wrap gap-1">
                  {DEVICE_FIELDS.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => onInsert(f)}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-0.5 text-xs text-blue-400 transition-colors hover:bg-blue-500/25"
                    >
                      {`{{${f}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 flex items-center gap-1 text-xs text-neutral-500">
                  <CreditCard className="size-3" /> Pagos
                </p>
                <div className="flex flex-wrap gap-1">
                  {PAYMENT_FIELDS.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => onInsert(f)}
                      className="inline-flex items-center gap-1 rounded-md bg-green-500/10 px-2 py-0.5 text-xs text-green-400 transition-colors hover:bg-green-500/25"
                    >
                      {`{{${f}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-neutral-600">
                Campos de dispositivos y pagos requieren JOINs en el query SQL del tipo de flujo.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-component: StepEditor ───────────────────────────────────────────────

function StepEditor({
  step,
  index,
  total,
  availableFields,
  messageTemplates,
  onChange,
  onRemove,
}: {
  step: LifecycleFlowStep;
  index: number;
  total: number;
  availableFields: string[];
  messageTemplates: MessageTemplate[];
  onChange: (updated: LifecycleFlowStep) => void;
  onRemove: () => void;
}) {
  const insertField = (fieldName: string, target: "title" | "body") => {
    const placeholder = `{{${fieldName}}}`;
    onChange({ ...step, [target]: step[target] + placeholder });
  };

  const applyTemplate = (templateId: string) => {
    const tmpl = messageTemplates.find((t) => t.id === templateId);
    if (tmpl) {
      onChange({
        ...step,
        title: tmpl.title || step.title,
        body: tmpl.body,
        channel: tmpl.channel as any,
      });
    }
  };

  const channelTemplates = messageTemplates.filter(
    (t) => t.channel === step.channel || t.channel === "all",
  );

  return (
    <div className="rounded-lg border border-neutral-700 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-neutral-200">
          Paso {index + 1}
        </h4>
        {total > 1 && (
          <button
            onClick={onRemove}
            className="text-xs text-red-400 hover:text-red-300"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select
            title="Canal"
            defaultValue={step.channel}
            onSelectedChange={(selected) =>
              onChange({ ...step, channel: (selected as "push" | "sms" | "whatsapp") || step.channel })
            }
            options={[
              { label: "Push", value: "push" },
              { label: "SMS", value: "sms" },
              { label: "WhatsApp", value: "whatsapp" },
            ]}
          />
          <Input
            placeholder="Dias despues del paso anterior"
            floatingLabel="Dias despues del paso anterior"
            type="number"
            value={String(step.delay_after_previous_days)}
            onChange={(val) =>
              onChange({
                ...step,
                delay_after_previous_days: parseInt(val) || 0,
              })
            }
          />
        </div>

        {channelTemplates.length > 0 && (
          <div>
            <label className="mb-1 block text-xs text-neutral-400">
              Usar plantilla
            </label>
            <select
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-200"
              value=""
              onChange={(e) => applyTemplate(e.target.value)}
            >
              <option value="">Seleccionar plantilla...</option>
              {channelTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {step.channel === "push" && (
          <div>
            <Textarea
              placeholder="Titulo"
              floatingLabel="Titulo"
              value={step.title}
              onChange={(val) =>
                onChange({ ...step, title: val })
              }
              rows={1}
            />
            <div className="mt-1">
              <AvailableFieldsBadges
                fields={availableFields}
                onInsert={(f) => insertField(f, "title")}
                showExtras
              />
            </div>
          </div>
        )}

        <div>
          <Textarea
            placeholder="Mensaje"
            floatingLabel="Mensaje"
            value={step.body}
            onChange={(val) => onChange({ ...step, body: val })}
            rows={3}
          />
          <div className="mt-1">
            <AvailableFieldsBadges
              fields={availableFields}
              onInsert={(f) => insertField(f, "body")}
              showExtras
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-neutral-300">
            <input
              type="checkbox"
              checked={step.generate_discount}
              onChange={(e) =>
                onChange({ ...step, generate_discount: e.target.checked })
              }
              className="rounded"
            />
            <Gift className="size-4" />
            Generar codigo de descuento
          </label>
        </div>

        {step.generate_discount && (
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-neutral-800/50 p-3">
            <Input
              placeholder="Valor descuento ($)"
              floatingLabel="Valor descuento ($)"
              type="number"
              value={String(step.discount_value_off)}
              onChange={(val) =>
                onChange({
                  ...step,
                  discount_value_off: parseFloat(val) || 5,
                })
              }
            />
            <Input
              placeholder="Dias de validez"
              floatingLabel="Dias de validez"
              type="number"
              value={String(step.discount_validity_days)}
              onChange={(val) =>
                onChange({
                  ...step,
                  discount_validity_days: parseInt(val) || 30,
                })
              }
            />
          </div>
        )}

        {/* Exit Conditions */}
        <div>
          <button
            type="button"
            onClick={() => {
              const conditions = step.exit_conditions || [];
              onChange({
                ...step,
                exit_conditions: [
                  ...conditions,
                  { field: "", operator: "is_not_null", value: "" },
                ],
              });
            }}
            className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-200"
          >
            <Shield className="size-3" />
            <Filter className="size-3" />
            Agregar condición de salida
          </button>
          {(step.exit_conditions || []).length > 0 && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-neutral-500">
                Si se cumple alguna condición, el usuario no recibirá este paso:
              </p>
              {(step.exit_conditions || []).map((cond, ci) => (
                <div
                  key={ci}
                  className="flex items-center gap-2 rounded-lg bg-neutral-800/50 p-2"
                >
                  <select
                    className="flex-1 rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs text-neutral-200"
                    value={cond.field}
                    onChange={(e) => {
                      const updated = [...(step.exit_conditions || [])];
                      updated[ci] = { ...cond, field: e.target.value };
                      onChange({ ...step, exit_conditions: updated });
                    }}
                  >
                    <option value="">Seleccionar campo...</option>
                    <optgroup label="Campos del query">
                      {availableFields.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Dispositivos">
                      {DEVICE_FIELDS.map((f) => (
                        <option key={`d_${f}`} value={f}>
                          {f}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Pagos">
                      {PAYMENT_FIELDS.map((f) => (
                        <option key={`p_${f}`} value={f}>
                          {f}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <select
                    className="w-32 shrink-0 rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs text-neutral-200"
                    value={cond.operator}
                    onChange={(e) => {
                      const updated = [...(step.exit_conditions || [])];
                      updated[ci] = {
                        ...cond,
                        operator: e.target.value as ExitCondition["operator"],
                      };
                      onChange({ ...step, exit_conditions: updated });
                    }}
                  >
                    {EXIT_CONDITION_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                  {cond.operator !== "is_null" &&
                    cond.operator !== "is_not_null" && (
                      <input
                        type="text"
                        className="flex-1 rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs text-neutral-200 placeholder:text-neutral-500"
                        placeholder="Valor"
                        value={cond.value}
                        onChange={(e) => {
                          const updated = [...(step.exit_conditions || [])];
                          updated[ci] = { ...cond, value: e.target.value };
                          onChange({ ...step, exit_conditions: updated });
                        }}
                      />
                    )}
                  <button
                    type="button"
                    onClick={() => {
                      const updated = (step.exit_conditions || []).filter(
                        (_, i) => i !== ci,
                      );
                      onChange({ ...step, exit_conditions: updated });
                    }}
                    className="shrink-0 rounded p-1 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component: Edit Flow Dialog ─────────────────────────────────────────

function EditFlowDialog({
  flow,
  flowTypes,
  messageTemplates,
  country,
  onClose,
  onSaved,
}: {
  flow: LifecycleFlow | null;
  flowTypes: FlowType[];
  messageTemplates: MessageTemplate[];
  country: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { internalCountry, userData } = useContext(GlobalContext);
  const isNew = !flow?.id;
  const [edited, setEdited] = useState<LifecycleFlow>(
    flow || {
      name: "",
      flow_type_id: "",
      flow_type: "",
      country,
      is_active: false,
      delay_days: 0,
      steps: [
        {
          step_order: 1,
          channel: "push",
          title: "",
          body: "",
          delay_after_previous_days: 0,
          generate_discount: false,
          discount_value_off: 5,
          discount_validity_days: 30,
        },
      ],
      bigquery_query: "",
    },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSqlPreview, setShowSqlPreview] = useState(false);

  const selectedFlowType = flowTypes.find(
    (ft) => ft.id === edited.flow_type_id,
  );
  const availableFields = selectedFlowType?.available_fields || [];

  const updateStep = (index: number, updated: LifecycleFlowStep) => {
    const newSteps = [...edited.steps];
    newSteps[index] = updated;
    setEdited({ ...edited, steps: newSteps });
  };

  const addStep = () => {
    setEdited({
      ...edited,
      steps: [
        ...edited.steps,
        {
          step_order: edited.steps.length + 1,
          channel: "push",
          title: "",
          body: "",
          delay_after_previous_days: 1,
          generate_discount: false,
          discount_value_off: 5,
          discount_validity_days: 30,
        },
      ],
    });
  };

  const removeStep = (index: number) => {
    const newSteps = edited.steps
      .filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, step_order: i + 1 }));
    setEdited({ ...edited, steps: newSteps });
  };

  const selectFlowType = (flowTypeId: string) => {
    const ft = flowTypes.find((f) => f.id === flowTypeId);
    setEdited({
      ...edited,
      flow_type_id: flowTypeId,
      flow_type: ft?.name || "",
      bigquery_query: "",
    });
  };

  const handleSave = async () => {
    if (!edited.name) {
      setError("El nombre es requerido");
      return;
    }
    if (!edited.flow_type_id) {
      setError("Selecciona un tipo de flujo");
      return;
    }
    if (edited.steps.length === 0) {
      setError("Agrega al menos un paso");
      return;
    }
    for (const step of edited.steps) {
      if (!step.body) {
        setError(`El paso ${step.step_order} necesita un mensaje`);
        return;
      }
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        name: edited.name,
        flow_type_id: edited.flow_type_id,
        flow_type: edited.flow_type,
        country: edited.country || country,
        is_active: edited.is_active,
        delay_days: edited.delay_days,
        steps: edited.steps,
        bigquery_query: edited.bigquery_query || "",
      };

      if (isNew) {
        await unsafeAPI("POST", "https://api.krece.app/lifecycle_flow/", payload, userData, internalCountry?.get() || country);
      } else {
        await unsafeAPI("PATCH", `https://api.krece.app/lifecycle_flow/${flow!.id}/`, payload, userData, internalCountry?.get() || country);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!flow?.id) return;
    setSaving(true);
    try {
      await unsafeAPI("DELETE", `https://api.krece.app/lifecycle_flow/${flow.id}/`, null, userData, internalCountry?.get() || country);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Error al eliminar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        title={isNew ? "Crear flujo" : "Editar flujo"}
        onClose={!saving ? onClose : undefined}
        className="max-w-[750px]"
      >
        <div className="space-y-5 p-1">
          {error && (
            <div className="rounded-lg bg-red-500/20 p-3 text-sm text-red-300">
              <AlertCircle className="mr-2 inline size-4" />
              {error}
            </div>
          )}

          {/* General */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-neutral-200">General</h3>
            <Input
              placeholder="Nombre del flujo"
              floatingLabel="Nombre del flujo"
              value={edited.name}
              onChange={(val) =>
                setEdited({ ...edited, name: val })
              }
            />
            <div>
              <label className="mb-1 block text-xs text-neutral-400">
                Tipo de flujo
              </label>
              <select
                className="w-full rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-200"
                value={edited.flow_type_id}
                onChange={(e) => selectFlowType(e.target.value)}
              >
                <option value="">Seleccionar tipo...</option>
                {flowTypes.map((ft) => (
                  <option key={ft.id} value={ft.id}>
                    {ft.display_name} - {ft.description.slice(0, 60)}...
                  </option>
                ))}
              </select>
            </div>

            {selectedFlowType && (
              <div className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${CATEGORY_COLORS[selectedFlowType.category] || CATEGORY_COLORS.custom}`}
                    >
                      {CATEGORY_LABELS[selectedFlowType.category] ||
                        selectedFlowType.category}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {selectedFlowType.name}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowSqlPreview(!showSqlPreview)}
                    className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
                  >
                    <Eye className="size-3" />
                    {showSqlPreview ? "Ocultar SQL" : "Ver SQL"}
                  </button>
                </div>
                <p className="text-xs text-neutral-400">
                  {selectedFlowType.description}
                </p>
                {showSqlPreview && (
                  <div className="mt-2">
                    <SqlPreview sql={selectedFlowType.bigquery_query} />
                  </div>
                )}
                {availableFields.length > 0 && (
                  <div className="mt-2">
                    <p className="mb-1 text-xs text-neutral-500">
                      Campos disponibles para mensajes:
                    </p>
                    <AvailableFieldsBadges fields={availableFields} />
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-neutral-300">
                <input
                  type="checkbox"
                  checked={edited.is_active}
                  onChange={(e) =>
                    setEdited({ ...edited, is_active: e.target.checked })
                  }
                  className="rounded"
                />
                Activo
              </label>
              <Input
                placeholder="Dias antes de activar (opcional)"
                floatingLabel="Dias antes de activar (opcional)"
                type="number"
                value={String(edited.delay_days)}
                onChange={(val) =>
                  setEdited({
                    ...edited,
                    delay_days: parseInt(val) || 0,
                  })
                }
              />
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-neutral-200">
                Pasos ({edited.steps.length})
              </h3>
              <button
                onClick={addStep}
                className="flex items-center gap-1 rounded-lg bg-primary-500/20 px-3 py-1 text-xs text-primary-300 hover:bg-primary-500/30"
              >
                <Plus className="size-3" /> Agregar paso
              </button>
            </div>

            {edited.steps.map((step, i) => (
              <StepEditor
                key={i}
                step={step}
                index={i}
                total={edited.steps.length}
                availableFields={availableFields}
                messageTemplates={messageTemplates}
                onChange={(updated) => updateStep(i, updated)}
                onRemove={() => removeStep(i)}
              />
            ))}
          </div>

          {/* Advanced */}
          <div>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-300"
            >
              {showAdvanced ? (
                <ChevronUp className="size-3" />
              ) : (
                <ChevronDown className="size-3" />
              )}
              Avanzado: Query personalizado
            </button>
            {showAdvanced && (
              <div className="mt-2">
                <Textarea
                  placeholder="BigQuery query personalizado"
                  floatingLabel="BigQuery query personalizado (sobreescribe el del tipo de flujo)"
                  value={edited.bigquery_query}
                  onChange={(val) =>
                    setEdited({
                      ...edited,
                      bigquery_query: val,
                    })
                  }
                  rows={6}
                />
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between border-t border-neutral-700 pt-4">
            {!isNew ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300"
                disabled={saving}
              >
                <Trash2 className="size-4" /> Eliminar
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button onClick={onClose} disabled={saving} variant="outline">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="mr-1 size-4" />
                {saving ? "Guardando..." : isNew ? "Crear" : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {showDeleteConfirm && (
        <ConfirmDialog
          title="Eliminar flujo"
          message="¿Estás seguro de que quieres eliminar este flujo? Esta acción no se puede deshacer."
          onConfirm={async () => { await handleDelete(); return true; }}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </>
  );
}

// ─── Sub-component: Edit Flow Type Dialog ────────────────────────────────────

function EditFlowTypeDialog({
  flowType,
  onClose,
  onSaved,
}: {
  flowType: FlowType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { internalCountry, userData } = useContext(GlobalContext);
  const isNew = !flowType?.id;
  const [edited, setEdited] = useState({
    name: flowType?.name || "",
    display_name: flowType?.display_name || "",
    description: flowType?.description || "",
    category: flowType?.category || "custom",
    bigquery_query: flowType?.bigquery_query || "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    if (!flowType?.id) return;
    setDeleting(true);
    setError("");
    try {
      await unsafeAPI(
        "DELETE",
        `https://api.krece.app/lifecycle_flow_type/${flowType.id}/`,
        null,
        userData,
        null,
      );
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (
      !edited.name ||
      !edited.display_name ||
      !edited.description ||
      !edited.bigquery_query
    ) {
      setError("Todos los campos son requeridos");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isNew) {
        await unsafeAPI("POST", "https://api.krece.app/lifecycle_flow_type/", edited, userData, null);
      } else {
        await unsafeAPI(
          "PATCH",
          `https://api.krece.app/lifecycle_flow_type/${flowType!.id}/`,
          edited,
          userData,
          null,
        );
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog
      title={isNew ? "Crear tipo de flujo" : "Editar tipo de flujo"}
      onClose={!saving ? onClose : undefined}
      className="max-w-[700px]"
    >
      <div className="space-y-4 p-1">
        {error && (
          <div className="rounded-lg bg-red-500/20 p-3 text-sm text-red-300">
            <AlertCircle className="mr-2 inline size-4" />
            {error}
          </div>
        )}

        <Input
          placeholder="Nombre interno (ej: activation_welcome)"
          floatingLabel="Nombre interno (ej: activation_welcome)"
          value={edited.name}
          onChange={(val) => setEdited({ ...edited, name: val })}
        />
        <Input
          placeholder="Nombre para mostrar"
          floatingLabel="Nombre para mostrar"
          value={edited.display_name}
          onChange={(val) =>
            setEdited({ ...edited, display_name: val })
          }
        />
        <Textarea
          placeholder="Descripción"
          floatingLabel="Descripción"
          value={edited.description}
          onChange={(val) =>
            setEdited({ ...edited, description: val })
          }
          rows={2}
        />
        <Select
          title="Categoría"
          defaultValue={edited.category}
          onSelectedChange={(selected) =>
            setEdited({ ...edited, category: (selected as string) || edited.category })
          }
          options={[
            { label: "Engagement", value: "engagement" },
            { label: "Crecimiento", value: "growth" },
            { label: "Retención", value: "retention" },
            { label: "Recuperación", value: "win_back" },
            { label: "Personalizado", value: "custom" },
          ]}
        />
        <div>
          <Textarea
            placeholder="BigQuery SQL Query"
            floatingLabel="BigQuery SQL Query"
            value={edited.bigquery_query}
            onChange={(val) =>
              setEdited({ ...edited, bigquery_query: val })
            }
            rows={10}
          />
          <p className="mt-1 text-xs text-neutral-500">
            Usa {"{country}"} y {"{delay_days}"} como placeholders. El SELECT
            debe incluir document_id, push_token, phone_number, country,
            first_name.
          </p>
        </div>

        <div className="flex items-center justify-between border-t border-neutral-700 pt-4">
          {!isNew ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300"
              disabled={saving || deleting}
            >
              <Trash2 className="size-4" /> Eliminar
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button onClick={onClose} disabled={saving || deleting} variant="outline">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving || deleting}>
              <Save className="mr-1 size-4" />
              {saving ? "Guardando..." : isNew ? "Crear" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
    {showDeleteConfirm && (
      <ConfirmDialog
        title="Eliminar tipo de flujo"
        message="¿Estás seguro de que quieres eliminar este tipo de flujo? Los flujos que lo usan dejarán de funcionar."
        onConfirm={async () => { await handleDelete(); return true; }}
        onClose={() => setShowDeleteConfirm(false)}
      />
    )}
  </>
  );
}

// ─── Tab: Flujos (Active Flows) ──────────────────────────────────────────────

function FlowsTab({
  flows,
  flowTypes,
  messageTemplates,
  country,
  loading,
}: {
  flows: LifecycleFlow[];
  flowTypes: FlowType[];
  messageTemplates: MessageTemplate[];
  country: string;
  loading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editingFlow, setEditingFlow] = useState<LifecycleFlow | null | "new">(
    null,
  );

  const getFlowType = (flow: LifecycleFlow) =>
    flowTypes.find((t) => t.id === flow.flow_type_id);

  const getFlowTypeName = (flow: LifecycleFlow) => {
    const ft = getFlowType(flow);
    return ft?.display_name || flow.flow_type || "—";
  };

  const getFlowCategory = (flow: LifecycleFlow) => {
    const ft = getFlowType(flow);
    return ft?.category || "custom";
  };

  const filtered = flows.filter((f) => {
    const matchesSearch =
      !search ||
      f.name?.toLowerCase().includes(search.toLowerCase()) ||
      f.flow_type?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      !categoryFilter || getFlowCategory(f) === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) return <ContainerSpinner />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-2.5 size-4 text-neutral-500" />
            <input
              type="text"
              placeholder="Buscar flujos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 py-2 pl-9 pr-3 text-sm text-neutral-200 placeholder:text-neutral-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-200"
          >
            <option value="">Todas las categorías</option>
            <option value="engagement">Engagement</option>
            <option value="growth">Crecimiento</option>
            <option value="retention">Retención</option>
            <option value="win_back">Recuperación</option>
            <option value="custom">Personalizado</option>
          </select>
        </div>
        <button
          onClick={() => setEditingFlow("new")}
          className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          <Plus className="size-4" /> Crear flujo
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="py-12 text-center text-neutral-500">
          <Bell className="mx-auto mb-2 size-8" />
          <p>No hay flujos creados</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((flow) => (
            <div
              key={flow.id}
              onClick={() => setEditingFlow(flow)}
              className="cursor-pointer rounded-lg border border-neutral-700 bg-neutral-800/50 p-4 transition-colors hover:border-primary-500/50"
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-neutral-200">{flow.name}</h3>
                <div className="flex items-center gap-1.5">
                  <span className="rounded-full bg-neutral-700/50 px-2 py-0.5 text-xs text-neutral-400">
                    {flow.country}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${flow.is_active ? "bg-green-500/20 text-green-400" : "bg-neutral-500/20 text-neutral-400"}`}
                  >
                    {flow.is_active ? "Activo" : "Inactivo"}
                  </span>
                </div>
              </div>
              <div className="mb-2 flex items-center gap-2">
                <p className="text-sm text-neutral-400">
                  {getFlowTypeName(flow)}
                </p>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${CATEGORY_COLORS[getFlowCategory(flow)] || CATEGORY_COLORS.custom}`}
                >
                  {CATEGORY_LABELS[getFlowCategory(flow)] || getFlowCategory(flow)}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-neutral-500">
                <span className="flex items-center gap-1">
                  <Send className="size-3" />
                  {flow.steps?.length || 0} pasos
                </span>
                {flow.delay_days > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="size-3" />
                    {flow.delay_days}d retraso
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editingFlow !== null && (
        <EditFlowDialog
          flow={editingFlow === "new" ? null : editingFlow}
          flowTypes={flowTypes}
          messageTemplates={messageTemplates}
          country={country}
          onClose={() => setEditingFlow(null)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}

// ─── Tab: Tipos de Flujo ─────────────────────────────────────────────────────

function FlowTypesTab({
  flowTypes,
  loading,
}: {
  flowTypes: FlowType[];
  loading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [editingType, setEditingType] = useState<FlowType | null | "new">(null);
  const [previewSql, setPreviewSql] = useState<string | null>(null);

  const filtered = flowTypes.filter(
    (ft) =>
      ft.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      ft.name?.toLowerCase().includes(search.toLowerCase()) ||
      ft.description?.toLowerCase().includes(search.toLowerCase()),
  );

  if (loading) return <ContainerSpinner />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="relative w-64">
          <Search className="absolute left-3 top-2.5 size-4 text-neutral-500" />
          <input
            type="text"
            placeholder="Buscar tipos de flujo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-neutral-700 bg-neutral-800 py-2 pl-9 pr-3 text-sm text-neutral-200 placeholder:text-neutral-500"
          />
        </div>
        <button
          onClick={() => setEditingType("new")}
          className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          <Plus className="size-4" /> Crear tipo de flujo
        </button>
      </div>

      <div className="space-y-3">
        {filtered.map((ft) => (
          <div
            key={ft.id}
            className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h3 className="font-medium text-neutral-200">
                    {ft.display_name}
                  </h3>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${CATEGORY_COLORS[ft.category] || CATEGORY_COLORS.custom}`}
                  >
                    {CATEGORY_LABELS[ft.category] || ft.category}
                  </span>
                  {ft.is_system && (
                    <span className="rounded-full bg-neutral-700 px-2 py-0.5 text-xs text-neutral-400">
                      Sistema
                    </span>
                  )}
                </div>
                <p className="mb-2 text-sm text-neutral-400">
                  {ft.description}
                </p>
                {ft.available_fields?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {ft.available_fields.map((f) => (
                      <span
                        key={f}
                        className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-500"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setPreviewSql(previewSql === ft.id ? null : ft.id)
                  }
                  className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200"
                  title="Ver SQL"
                >
                  <Eye className="size-4" />
                </button>
                <button
                  onClick={() => setEditingType(ft)}
                  className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200"
                  title="Editar"
                >
                  <Pencil className="size-4" />
                </button>
              </div>
            </div>
            {previewSql === ft.id && (
              <div className="mt-3">
                <SqlPreview sql={ft.bigquery_query} maxHeight="max-h-64" />
              </div>
            )}
          </div>
        ))}
      </div>

      {editingType !== null && (
        <EditFlowTypeDialog
          flowType={editingType === "new" ? null : editingType}
          onClose={() => setEditingType(null)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}

// ─── Tab: Execution History ──────────────────────────────────────────────────

function ExecutionHistoryTab({
  flows,
  country,
}: {
  flows: LifecycleFlow[];
  country: string;
}) {
  const { userData } = useContext(GlobalContext);
  const [selectedFlow, setSelectedFlow] = useState("");
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchExecutions = async (flowId: string) => {
    setLoading(true);
    try {
      const res = await unsafeAPI(
        "GET",
        `https://api.krece.app/lifecycle_flow_executions/${flowId}/`,
        null,
        userData,
        country,
      );
      setExecutions(res?.executions || []);
    } catch {
      setExecutions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedFlow) fetchExecutions(selectedFlow);
  }, [selectedFlow]);

  return (
    <div>
      <div className="mb-4">
        <Select
          title="Seleccionar flujo"
          defaultValue={selectedFlow || undefined}
          placeholder="Seleccionar..."
          onSelectedChange={(selected) =>
            setSelectedFlow(typeof selected === "string" ? selected : "")
          }
          options={flows.map((f) => ({
            label: f.name,
            value: f.id || "",
          }))}
        />
      </div>

      {loading ? (
        <ContainerSpinner />
      ) : executions.length === 0 ? (
        <div className="py-12 text-center text-neutral-500">
          <Activity className="mx-auto mb-2 size-8" />
          <p>
            {selectedFlow
              ? "No hay ejecuciones registradas"
              : "Selecciona un flujo"}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-700 text-left text-neutral-400">
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Paso</th>
                <th className="px-3 py-2">Canal</th>
                <th className="px-3 py-2">Usuarios</th>
                <th className="px-3 py-2">Enviados</th>
                <th className="px-3 py-2">Fallidos</th>
                <th className="px-3 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((exec) => (
                <tr
                  key={exec.id}
                  className="border-b border-neutral-800 text-neutral-300"
                >
                  <td className="px-3 py-2 text-xs">
                    {exec.started_at
                      ? new Date(exec.started_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2">{exec.step_index}</td>
                  <td className="px-3 py-2 capitalize">{exec.channel}</td>
                  <td className="px-3 py-2">{exec.users_targeted}</td>
                  <td className="px-3 py-2 text-green-400">
                    {exec.notifications_sent}
                  </td>
                  <td className="px-3 py-2 text-red-400">
                    {exec.notifications_failed}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        exec.status === "running"
                          ? "bg-blue-500/20 text-blue-400"
                          : exec.status === "completed"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {exec.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Metrics ────────────────────────────────────────────────────────────

function MetricsTab({ country }: { country: string }) {
  const { userData } = useContext(GlobalContext);
  const [metrics, setMetrics] = useState<FlowMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await unsafeAPI(
          "GET",
          `https://api.krece.app/lifecycle_flow_metrics/?country=${country}`,
          null,
          userData,
          country,
        );
        setMetrics(res?.metrics || []);
      } catch {
        setMetrics([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [country]);

  if (loading) return <ContainerSpinner />;

  const totalSent = metrics.reduce((sum, m) => sum + m.total_sent, 0);
  const totalTargeted = metrics.reduce((sum, m) => sum + m.total_targeted, 0);
  const totalFailed = metrics.reduce((sum, m) => sum + m.total_failed, 0);
  const activeFlows = metrics.filter((m) => m.is_active).length;

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
          <p className="text-xs text-neutral-500">Flujos activos</p>
          <p className="text-2xl font-bold text-neutral-200">{activeFlows}</p>
        </div>
        <div className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
          <p className="text-xs text-neutral-500">Usuarios alcanzados</p>
          <p className="text-2xl font-bold text-neutral-200">
            {totalTargeted.toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
          <p className="text-xs text-neutral-500">Enviados (30d)</p>
          <p className="text-2xl font-bold text-green-400">
            {totalSent.toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
          <p className="text-xs text-neutral-500">Fallidos (30d)</p>
          <p className="text-2xl font-bold text-red-400">
            {totalFailed.toLocaleString()}
          </p>
        </div>
      </div>

      {metrics.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-700 text-left text-neutral-400">
                <th className="px-3 py-2">Flujo</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Ejecuciones</th>
                <th className="px-3 py-2">Alcanzados</th>
                <th className="px-3 py-2">Enviados</th>
                <th className="px-3 py-2">Fallidos</th>
                <th className="px-3 py-2">Tasa éxito</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => {
                const rate =
                  m.total_sent + m.total_failed > 0
                    ? Math.round(
                        (m.total_sent / (m.total_sent + m.total_failed)) * 100,
                      )
                    : 0;
                return (
                  <tr
                    key={m.flow_id}
                    className="border-b border-neutral-800 text-neutral-300"
                  >
                    <td className="px-3 py-2">{m.flow_name}</td>
                    <td className="px-3 py-2 text-neutral-400">
                      {m.flow_type}
                    </td>
                    <td className="px-3 py-2">{m.execution_count}</td>
                    <td className="px-3 py-2">
                      {m.total_targeted.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-green-400">
                      {m.total_sent.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-red-400">
                      {m.total_failed.toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          rate >= 90
                            ? "text-green-400"
                            : rate >= 70
                              ? "text-yellow-400"
                              : "text-red-400"
                        }
                      >
                        {rate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const TABS = [
  { key: "flows", label: "Flujos", icon: Bell },
  { key: "types", label: "Tipos de flujo", icon: FileText },
  { key: "history", label: "Historial", icon: Activity },
  { key: "metrics", label: "Metricas", icon: BarChart3 },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function LifecycleNotifications() {
  const { internalCountry, userData } = useContext(GlobalContext);
  const country = internalCountry?.get() || "venezuela";

  const [activeTab, setActiveTab] = useState<TabKey>("flows");
  const [flows, setFlows] = useState<LifecycleFlow[]>([]);
  const [flowTypes, setFlowTypes] = useState<FlowType[]>([]);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>(
    [],
  );
  const [loadingFlows, setLoadingFlows] = useState(true);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Real-time listener for flows filtered by country
  useEffect(() => {
    setLoadingFlows(true);
    const unsubscribe = firestore
      .collection("LifecycleFlows")
      .where("country", "==", country)
      .onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as LifecycleFlow[];
        setFlows(data);
        setLoadingFlows(false);
      });
    return () => unsubscribe();
  }, [country]);

  // Fetch flow types from API (auto-seeds if empty)
  useEffect(() => {
    setLoadingTypes(true);
    unsafeAPI("GET", "https://api.krece.app/lifecycle_flow_types/", null, userData, null)
      .then((res: any) => {
        setFlowTypes(res?.flow_types || []);
      })
      .catch(() => setFlowTypes([]))
      .finally(() => setLoadingTypes(false));
  }, []);

  // Fetch message templates from API (auto-seeds if empty)
  useEffect(() => {
    setLoadingTemplates(true);
    unsafeAPI("GET", "https://api.krece.app/lifecycle_message_templates/", null, userData, country)
      .then((res: any) => {
        setMessageTemplates(res?.templates || []);
      })
      .catch(() => setMessageTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-100">
          Notificaciones Ciclo de Vida
        </h1>
        <p className="text-sm text-neutral-400">
          Gestiona flujos de notificaciones automatizados por segmento de
          usuarios
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 rounded-lg border border-neutral-700 bg-neutral-800/50 p-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-primary-500/20 text-primary-300"
                  : "text-neutral-400 hover:text-neutral-200"
              }`}
            >
              <Icon className="size-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "flows" && (
        <FlowsTab
          flows={flows}
          flowTypes={flowTypes}
          messageTemplates={messageTemplates}
          country={country}
          loading={loadingFlows || loadingTypes || loadingTemplates}
        />
      )}
      {activeTab === "types" && (
        <FlowTypesTab flowTypes={flowTypes} loading={loadingTypes} />
      )}
      {activeTab === "history" && (
        <ExecutionHistoryTab flows={flows} country={country} />
      )}
      {activeTab === "metrics" && <MetricsTab country={country} />}
    </div>
  );
}
